import type { NoticeType } from '@/features/teacher/types/notices';
import type { InstituteNotice, InstituteNoticeList } from '@/types/instituteNotice';
import type { StudentNotice, StudentNoticeList } from '@/types/student';

/**
 * A notice as this panel reads it, whichever board it came from.
 *
 * A child does not care that their class teacher's announcements and the
 * office's live in two tables; they care what they have been told. So the two
 * responses are mapped onto one shape here, once, and every component below
 * reads that shape rather than branching on the source (CLAUDE.md §3).
 *
 * **Every field is something the database actually holds.** This module used to
 * sit beside a `noticeData.ts` that invented the rest of it — a venue, a time,
 * bullet points, a closing note, PDF attachments and a sign-off, all guessed
 * from keywords in the title, plus eight sample notices shown whenever the real
 * board was empty. A child reading "Venue: School Campus, 09:00 AM to 04:00 PM"
 * on a notice whose teacher wrote no such thing is being told something untrue,
 * and neither `class_notices` nor `institute_notices` has a column for any of
 * it. Anything added here has to come back from the server first.
 */
export interface UnifiedNotice {
  uuid: string;
  title: string;
  /** Empty when the notice is its own headline, which most of them are. */
  body: string;
  noticeType: NoticeType;
  /** `YYYY-MM-DD` — the day it is *about*, not the day it was typed. */
  noticeDate: string;
  /** Full ISO instant it went up, for the time-ago line. */
  postedAt: string;
  /** The teacher, or whoever in the office put it up. */
  author: string;
  /** Which board it came from — the panel offers it as a filter. */
  source: 'CLASS' | 'INSTITUTE';
  /** Who it reaches: this child's section, or the audience the office chose. */
  reaches: string;
  /** Null unless the notice is about one subject. Only class notices carry it. */
  subject: string | null;
  /** Pinned by the office. Only institute notices can be; a class notice never is. */
  isPinned: boolean;
}

/**
 * The class board — what this child's own teachers have pinned up.
 *
 * `section.displayName` is who it reached, and it is the server's answer about
 * this child rather than a class named in the request.
 */
export function fromClassNotices(list: StudentNoticeList | null): UnifiedNotice[] {
  const sectionName = list?.section?.displayName ?? 'Your class';

  return (list?.notices ?? []).map((notice: StudentNotice) => ({
    uuid: notice.uuid,
    title: notice.title,
    body: notice.body,
    noticeType: notice.noticeType,
    noticeDate: notice.noticeDate,
    postedAt: notice.postedAt,
    author: notice.teacher,
    source: 'CLASS',
    // A notice pinned to the section is for that section; one the teacher left
    // unaddressed went to every class they take, and saying so is honest about
    // why a child two sections over was told the same thing.
    reaches: notice.forThisClass ? sectionName : 'All classes this teacher takes',
    subject: notice.subject?.name ?? null,
    isPinned: false,
  }));
}

/** The institute board — what the office has put up for this child's audience. */
export function fromInstituteNotices(list: InstituteNoticeList | null): UnifiedNotice[] {
  return (list?.notices ?? []).map((notice: InstituteNotice) => ({
    uuid: notice.uuid,
    title: notice.title,
    body: notice.body,
    noticeType: notice.noticeType,
    noticeDate: notice.noticeDate,
    postedAt: notice.createdAt,
    author: notice.postedBy,
    source: 'INSTITUTE',
    /** Worded by the server, so every console says it the same way. */
    reaches: notice.audienceLabel,
    // The office board is not about one subject — there is no column for it.
    subject: null,
    isPinned: notice.isPinned,
  }));
}
